import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, Mail, CheckCircle2, XCircle, MinusCircle } from "lucide-react";

const KIND_LABEL = {
  announcement: "Announcement", announcement_test: "Announcement (test)", weekly_reminder: "Friday reminder", reminder_test: "Friday reminder (test)",
  recording_published: "Replay notice", welcome: "Welcome", join_received: "Join request received", join_notify: "Join request (to you)",
  password_reset: "Password reset", set_password_invite: "Set-password invite", temp_password: "Temporary password", inquiry_notify: "Inquiry (to you)", download_ready: "MP4 ready", other: "Email",
};
const EVENT_STYLE = {
  delivered: "border-emerald-200 bg-emerald-50 text-emerald-700", opened: "border-emerald-200 bg-emerald-50 text-emerald-700", clicked: "border-emerald-200 bg-emerald-50 text-emerald-700",
  bounced: "border-red-200 bg-red-50 text-red-700", complained: "border-red-200 bg-red-50 text-red-700", delivery_delayed: "border-amber-200 bg-amber-50 text-amber-800",
};
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function StatusPill({ row }) {
  if (row.status === "failed") return <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700" title={row.error || ""}><XCircle className="mr-1 h-3 w-3" />Failed</Badge>;
  if (row.status === "skipped") return <Badge variant="outline" className="text-muted-foreground" title={row.error || ""}><MinusCircle className="mr-1 h-3 w-3" />Skipped</Badge>;
  if (row.last_event) return <Badge variant="outline" className={EVENT_STYLE[row.last_event] || "text-muted-foreground"}>{row.last_event.replace("_", " ")}</Badge>;
  return <Badge variant="outline" className="text-muted-foreground"><CheckCircle2 className="mr-1 h-3 w-3" />Sent</Badge>;
}

function Campaigns({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="email-campaigns">
      {rows.slice(0, 6).map((c) => (
        <div key={c.campaign_id} className="rounded-xl border border-border bg-card p-4" data-testid="email-campaign-card">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-sm font-medium text-foreground" title={c.subject}>{c.subject}</p>
            <Badge variant="outline" className={c.status === "sending" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-700"}>{c.status === "sending" ? "Sending…" : "Sent"}</Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{KIND_LABEL[c.kind] || c.kind} · {c.audience_label}{c.auto ? " · automatic" : ""}</p>
          <p className="mt-2 text-xs tabular-nums text-muted-foreground"><span className="font-medium text-foreground">{c.sent_count}</span> of {c.recipients_count} sent{c.failed_count ? ` · ${c.failed_count} failed` : ""} · {when(c.created_at)}</p>
        </div>
      ))}
    </div>
  );
}

export default function SentLog({ refreshKey = 0 }) {
  const [log, setLog] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/admin/email/log?limit=150"), api.get("/admin/email/campaigns")]);
    setLog(l.data); setCampaigns(c.data);
  };
  useEffect(() => { load().catch(() => toast.error("Couldn't load the send log")); }, [refreshKey]);
  useEffect(() => {
    if (!campaigns.some((c) => c.status === "sending")) return undefined;
    const t = setInterval(() => load().catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [campaigns]);

  const refreshStatuses = async () => {
    setRefreshing(true);
    try {
      const r = (await api.post("/admin/email/log/refresh")).data;
      if (r.error) toast.message("Delivery status unavailable", { description: r.error, duration: 12000 });
      else toast.success(r.updated ? `Updated ${r.updated} delivery status${r.updated === 1 ? "" : "es"}` : "Nothing new from Resend yet");
      await load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not refresh"); }
    finally { setRefreshing(false); }
  };

  if (!log) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-5" data-testid="sent-log">
      <Campaigns rows={campaigns} />
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" data-testid="sent-log-count">{log.length} most recent emails, newest first — including the automatic ones.</p>
        <Button variant="outline" size="sm" onClick={refreshStatuses} disabled={refreshing} data-testid="sent-log-refresh-button">{refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-2 h-4 w-4" />Check delivery</>}</Button>
      </div>
      {log.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="sent-log-empty"><Mail className="h-6 w-6" />Nothing sent yet. Every email — automatic or composed — will show up here.</div>
      ) : (
        <div className="overflow-auto rounded-xl border border-border">
          <Table data-testid="sent-log-table">
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Type</TableHead><TableHead>To</TableHead><TableHead className="hidden md:table-cell">Subject</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {log.map((r) => (
                <TableRow key={r.log_id} data-testid="sent-log-row">
                  <TableCell className="whitespace-nowrap py-2 text-xs text-muted-foreground">{when(r.created_at)}</TableCell>
                  <TableCell className="py-2 text-xs text-foreground">{KIND_LABEL[r.kind] || r.kind}</TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground">{r.to}</TableCell>
                  <TableCell className="hidden max-w-[320px] truncate py-2 text-xs text-muted-foreground md:table-cell" title={r.subject}>{r.subject}</TableCell>
                  <TableCell className="py-2"><StatusPill row={r} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
